/**
 * SAT AURA™ · Client Journey (M3 · INTERNAL PREVIEW)
 *
 * Progressive Journey: Intro → Consent → Reality Scan → Events →
 * Analyse-Warte-State → Review-Pending → Profile Reveal.
 * Mobile-first. Kein Pseudo-Prozente. Confidence-Bänder.
 */
import React, { useEffect, useState } from "react";
import {
  auraStartSession, auraGetCurrentSession, auraGrantConsent,
  auraUpsertRealityScan, auraAppendEvent, auraFinalize,
  auraGetProfile, auraDownloadReport, auraAttachResonance,
} from "../../lib/auraApi";

const DOMAINS = [
  ["REL", "Partnerschaft & Liebe"],
  ["FAM", "Familie & Herkunft"],
  ["SOC", "Freundschaften"],
  ["CAR", "Beruf & Karriere"],
  ["FIN", "Geld & Finanzen"],
  ["SEL", "Selbstbild & Selbstwert"],
  ["BND", "Grenzen"],
  ["DEC", "Entscheidungen"],
  ["ACH", "Leistung & Erfolg"],
  ["PUR", "Sinn & Berufung"],
  ["LIF", "Lebensgestaltung"],
];

const STEPS = ["intro", "consent", "reality", "events", "analysis",
                "review_pending", "profile"];

const bandColor = (band) => ({
  STRONG:   "text-emerald-600",
  MODERATE: "text-amber-600",
  LOW:      "text-slate-500",
}[band] || "text-slate-500");

const stepColor = (status) => ({
  OBSERVED: "text-emerald-700",
  INFERRED: "text-amber-700",
  INSUFFICIENT_EVIDENCE: "text-slate-500",
  MISSING: "text-slate-400 italic",
}[status] || "text-slate-500");


export default function AuraJourney() {
  const [step, setStep] = useState("intro");
  const [session, setSession] = useState(null);
  const [scan, setScan] = useState({});           // {DOMAIN_ID: form}
  const [events, setEvents] = useState([]);        // local buffer
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    auraGetCurrentSession().then(setSession).catch(() => {});
  }, []);

  useEffect(() => {
    if (session?.state === "RELEASED" && step !== "profile") {
      setStep("profile");
      auraGetProfile().then(setProfile).catch(() => {});
    }
  }, [session, step]);

  const advance = (to) => setStep(to);

  // -------- Intro ----------
  if (step === "intro") return (
    <div data-testid="aura-intro" className="max-w-2xl mx-auto p-8 space-y-8">
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-widest text-slate-500">
          DECODE · SAT AURA™
        </div>
        <h1 className="text-4xl font-serif text-slate-900">
          Warum passiert manches immer wieder?
        </h1>
        <p className="text-slate-600 leading-relaxed">
          AURA sucht keine schnellen Erklärungen. Es sucht belastbare
          wiederkehrende Muster – als Arbeitshypothesen, nicht als
          Etiketten. Ein Ereignis ist noch kein Muster.
        </p>
      </div>
      <div className="rounded-2xl bg-slate-50 p-6 space-y-2 text-sm">
        <div className="font-semibold">Der AURA Loop</div>
        <div className="text-slate-600">
          Trigger → Interpretation → Emotion → Verhalten → Ergebnis →
          Verstärkung
        </div>
      </div>
      <button
        data-testid="aura-start-journey"
        onClick={async () => { await auraStartSession(); advance("consent"); }}
        className="w-full rounded-full bg-slate-900 text-white py-3 font-medium"
      >
        Meine AURA-Reise starten
      </button>
    </div>
  );

  // -------- Consent ----------
  if (step === "consent") return (
    <div data-testid="aura-consent" className="max-w-2xl mx-auto p-8 space-y-6">
      <div className="text-xs uppercase tracking-widest text-amber-700">
        INTERNAL PREVIEW · nicht rechtlich freigegeben
      </div>
      <h1 className="text-3xl font-serif">Bevor wir starten</h1>
      <div className="text-sm text-slate-600 space-y-3">
        <p>
          AURA ist ein Reflexionsmodell. Es stellt keine psychologische
          oder medizinische Diagnose und ersetzt keine Behandlung.
        </p>
        <p>
          Deine Angaben werden ausschließlich zur Erstellung deiner AURA-
          Auswertung verwendet. Du kannst deine Zustimmung jederzeit
          widerrufen. Deine Resonanz zu einem gefundenen Muster verändert
          nicht automatisch die Evidenzbewertung.
        </p>
      </div>
      <button
        data-testid="aura-consent-accept"
        onClick={async () => { await auraGrantConsent(); advance("reality"); }}
        className="w-full rounded-full bg-slate-900 text-white py-3 font-medium"
      >
        Ich stimme zu und starte
      </button>
    </div>
  );

  // -------- Reality Scan ----------
  if (step === "reality") return (
    <div data-testid="aura-reality" className="max-w-3xl mx-auto p-8 space-y-6">
      <h1 className="text-3xl font-serif">Dein aktueller Lebensmoment</h1>
      <p className="text-slate-600 text-sm">
        Für welche Lebensbereiche lohnt sich aktuell ein genauerer Blick?
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        {DOMAINS.map(([id, label]) => (
          <button
            key={id}
            data-testid={`aura-domain-${id}`}
            onClick={() => setScan((s) => ({ ...s, [id]: s[id] || { open: true }}))}
            className={`text-left rounded-xl border p-4 hover:shadow ${
              scan[id] ? "border-slate-900" : "border-slate-200"}`}
          >
            <div className="font-medium">{label}</div>
            <div className="text-xs text-slate-500">{id}</div>
          </button>
        ))}
      </div>
      {Object.entries(scan).filter(([_, v]) => v.open).map(([id, v]) => (
        <ScanForm
          key={id} domainId={id}
          onSubmit={async (payload) => {
            await auraUpsertRealityScan(payload);
            setScan((s) => ({ ...s, [id]: { open: false, done: true }}));
          }}
        />
      ))}
      <button
        data-testid="aura-reality-continue"
        disabled={!Object.values(scan).some((v) => v.done)}
        onClick={() => advance("events")}
        className="w-full rounded-full bg-slate-900 text-white py-3 font-medium disabled:opacity-40"
      >
        Weiter zu deinen Ereignissen
      </button>
    </div>
  );

  // -------- Events ----------
  if (step === "events") return (
    <div data-testid="aura-events" className="max-w-3xl mx-auto p-8 space-y-6">
      <h1 className="text-3xl font-serif">Konkrete Situationen</h1>
      <p className="text-slate-600 text-sm">
        Bitte beschreibe mindestens drei konkrete Situationen, in denen
        sich etwas Ähnliches wiederholt. Auch Ausnahmen sind wichtig.
      </p>
      <EventForm onSubmit={async (payload) => {
        const ev = await auraAppendEvent(payload);
        setEvents((es) => [...es, ev]);
      }} />
      <div className="text-sm text-slate-600" data-testid="aura-events-count">
        Erfasst: {events.length}
      </div>
      <button
        data-testid="aura-finalize"
        disabled={events.length < 2}
        onClick={async () => {
          advance("analysis");
          await auraFinalize();
          setTimeout(() => advance("review_pending"), 1200);
        }}
        className="w-full rounded-full bg-slate-900 text-white py-3 font-medium disabled:opacity-40"
      >
        Zur AURA-Analyse
      </button>
    </div>
  );

  // -------- Analysis Wait ----------
  if (step === "analysis") return (
    <div data-testid="aura-analysis" className="max-w-xl mx-auto p-12 text-center space-y-4">
      <h1 className="text-3xl font-serif">Wir suchen nicht nach schnellen Antworten.</h1>
      <ul className="text-sm text-slate-600 space-y-2 text-left mt-6">
        <li>· Wiederholungen werden verglichen</li>
        <li>· Unterschiede zwischen Lebensbereichen geprüft</li>
        <li>· Gegenbeispiele einbezogen</li>
        <li>· Mögliche Muster herausgearbeitet</li>
      </ul>
      <div className="text-xs text-slate-400 mt-8">
        Ergebnisse werden vor der Freigabe von einer Mentor:in geprüft.
      </div>
    </div>
  );

  if (step === "review_pending") return (
    <div data-testid="aura-review-pending" className="max-w-xl mx-auto p-12 text-center space-y-4">
      <h1 className="text-3xl font-serif">Deine Auswertung wird vervollständigt.</h1>
      <p className="text-slate-600">
        Eine SAT-Mentor:in prüft aktuell deine AURA-Hypothesen. Du erhältst
        eine Nachricht, sobald deine Auswertung freigegeben ist.
      </p>
    </div>
  );

  // -------- Profile Reveal ----------
  if (step === "profile" && profile) return (
    <div data-testid="aura-profile" className="max-w-4xl mx-auto p-8 space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500">
          Deine AURA Map
        </div>
        <h1 className="text-4xl font-serif mt-2">
          Die wiederkehrenden Mechanismen deiner Lebensrealität
        </h1>
        <p className="text-slate-600 mt-2">
          Sichtbar gemacht als Arbeitshypothesen, nicht als Etiketten.
          Overall Confidence:
          <span className={`ml-2 font-semibold ${bandColor(profile.overall_confidence_band)}`}
                data-testid="aura-overall-band">
            {profile.overall_confidence_band}
          </span>
        </p>
      </div>

      <section data-testid="aura-patterns" className="space-y-3">
        <h2 className="text-lg font-semibold">Pattern-Kandidaten</h2>
        {profile.pattern_candidates.length === 0 && (
          <p className="text-sm text-slate-500">
            Kein Kandidat erfüllt die Evidenz-Kriterien. Das ist möglich —
            und ehrlicher als eine erfundene Erklärung.
          </p>
        )}
        {profile.pattern_candidates.map((c) => (
          <div key={c.candidate_id}
               className="rounded-xl border border-slate-200 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="font-medium">{c.pattern_family}</div>
              <div className={`text-xs ${bandColor(c.confidence_band)}`}>
                Evidence: {c.confidence_band}
              </div>
            </div>
            <div className="text-xs text-slate-500">
              Status: {c.state} · Domains: {c.domains.join(", ")} ·
              Gegenevidenz-Impact: {c.counter_evidence_impact_band}
            </div>
            <ResonanceControl
              profileId={profile.profile_id}
              candidateId={c.candidate_id}
              currentResonance={c.client_resonance}
            />
          </div>
        ))}
      </section>

      {profile.cross_domain_patterns.length > 0 && (
        <section data-testid="aura-cross-domain">
          <h2 className="text-lg font-semibold">Cross-Domain-Muster</h2>
          {profile.cross_domain_patterns.map((cd, i) => (
            <div key={i} className="text-sm text-slate-600">
              {cd.pattern_family} · {cd.domains.join(" · ")} · {cd.confidence_band}
            </div>
          ))}
        </section>
      )}

      {profile.meta_pattern_candidates.length > 0 && (
        <section data-testid="aura-meta">
          <h2 className="text-lg font-semibold">Möglicher gemeinsamer Mechanismus</h2>
          {profile.meta_pattern_candidates.map((m) => (
            <div key={m.meta_pattern_id} className="rounded-xl bg-slate-50 p-4">
              <div className="font-medium">{m.mechanism_label}</div>
              <div className="text-xs text-slate-500">
                Domains: {m.domains_covered.join(", ")} · Challenge: {m.challenge_result}
              </div>
            </div>
          ))}
        </section>
      )}

      {profile.breakpoint_candidates.length > 0 && (
        <section data-testid="aura-breakpoints">
          <h2 className="text-lg font-semibold">Wo Veränderung ansetzen könnte</h2>
          <p className="text-xs text-slate-500 mb-2">
            AURA identifiziert keinen fertigen Lösungsweg — nur mögliche Hebel.
          </p>
          {profile.breakpoint_candidates.map((b, i) => (
            <div key={i} className="text-sm text-slate-600">
              ● {b.kind} · Priorität: {b.priority_band}
            </div>
          ))}
        </section>
      )}

      <button
        data-testid="aura-download-report"
        onClick={() => auraDownloadReport()}
        className="rounded-full bg-slate-900 text-white px-6 py-2 text-sm"
      >
        AURA-Bericht als PDF laden
      </button>

      <div className="text-xs text-slate-400 pt-8 border-t">
        Nächster Schritt: TRANSFORM · HTC (Transform the Code) — folgt.
      </div>
    </div>
  );

  return <div>Lade …</div>;
}


function ScanForm({ domainId, onSubmit }) {
  const [f, setF] = useState({
    importance: 0.7, satisfaction: 0.5,
    subjective_control: 0.5, awareness: 0.5,
  });
  return (
    <div className="rounded-xl border p-4 space-y-3" data-testid={`aura-scan-${domainId}`}>
      <div className="font-medium">{domainId}</div>
      <label className="block text-sm">Wie wichtig ist dir dieser Bereich?
        <input type="range" min="0" max="1" step="0.1" value={f.importance}
                onChange={(e) => setF({ ...f, importance: parseFloat(e.target.value) })}
                className="w-full" data-testid={`aura-imp-${domainId}`} />
      </label>
      <label className="block text-sm">Wie zufrieden bist du aktuell damit?
        <input type="range" min="0" max="1" step="0.1" value={f.satisfaction}
                onChange={(e) => setF({ ...f, satisfaction: parseFloat(e.target.value) })}
                className="w-full" data-testid={`aura-sat-${domainId}`} />
      </label>
      <button
        onClick={() => onSubmit({ domain: domainId, ...f })}
        className="rounded-full bg-slate-900 text-white px-4 py-2 text-sm"
        data-testid={`aura-scan-submit-${domainId}`}
      >
        Speichern
      </button>
    </div>
  );
}


function EventForm({ onSubmit }) {
  const [e, setE] = useState({
    domain: "REL",
    event_date_or_period: new Date().toISOString().slice(0, 10),
    situation: "", relationship_role: "",
    trigger: "", automatic_interpretation: "",
    primary_emotion: "", actual_behavior: "",
    protective_strategy: "", short_term_gain: "",
    long_term_cost: "", outcome: "", reinforced_assumption: "",
    emotion_intensity: 0.6, subjective_importance: 0.6,
    recurrence_estimate: 3,
  });
  const set = (k, v) => setE({ ...e, [k]: v });
  const Field = ({ k, label, placeholder }) => (
    <label className="block text-sm">
      {label}
      <input value={e[k]} onChange={(ev) => set(k, ev.target.value)}
              placeholder={placeholder}
              data-testid={`aura-event-${k}`}
              className="w-full border rounded p-2 mt-1" />
    </label>
  );
  return (
    <div className="rounded-xl border p-4 space-y-3" data-testid="aura-event-form">
      <div className="grid md:grid-cols-2 gap-3">
        <label className="block text-sm">
          Lebensbereich
          <select value={e.domain} onChange={(ev) => set("domain", ev.target.value)}
                    className="w-full border rounded p-2 mt-1"
                    data-testid="aura-event-domain">
            {DOMAINS.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
          </select>
        </label>
        <Field k="event_date_or_period" label="Wann?" placeholder="YYYY-MM-DD" />
      </div>
      <Field k="trigger" label="Was war der Auslöser?" />
      <Field k="automatic_interpretation" label="Was dachtest du in diesem Moment?" />
      <Field k="primary_emotion" label="Was hast du gefühlt?" />
      <Field k="actual_behavior" label="Was hast du tatsächlich getan?" />
      <Field k="protective_strategy" label="Welche Strategie steckte darin?" />
      <Field k="outcome" label="Was war das Ergebnis?" />
      <Field k="reinforced_assumption"
                label="Was schien sich dadurch für dich zu bestätigen?" />
      <button
        onClick={() => onSubmit(e)}
        className="rounded-full bg-slate-900 text-white px-4 py-2 text-sm"
        data-testid="aura-event-submit"
      >
        Situation speichern
      </button>
    </div>
  );
}


function ResonanceControl({ profileId, candidateId, currentResonance }) {
  const [val, setVal] = useState(currentResonance ?? 0.5);
  const [saved, setSaved] = useState(currentResonance != null);
  return (
    <div className="text-xs text-slate-600 space-y-1"
          data-testid={`aura-resonance-${candidateId}`}>
      <div>Wie sehr erkennst du dich darin wieder?</div>
      <input type="range" min="0" max="1" step="0.1" value={val}
              onChange={(e) => setVal(parseFloat(e.target.value))}
              className="w-full" />
      <div className="flex items-center gap-2">
        <button
          onClick={async () => {
            await auraAttachResonance(profileId, candidateId, val);
            setSaved(true);
          }}
          data-testid={`aura-resonance-submit-${candidateId}`}
          className="text-xs bg-slate-100 rounded px-2 py-1"
        >
          Meine Resonanz speichern
        </button>
        {saved && <span className="text-emerald-600">gespeichert</span>}
      </div>
      <div className="text-slate-400 italic">
        Deine Resonanz verändert nicht automatisch die Evidenzbewertung.
      </div>
    </div>
  );
}
